// WS8 WS-5 — create-TPS modal. #3-A: the linked Entity is now OPTIONAL — a
// servicing firm can carry its own name/email/phone directly. Submit creates
// the TPS row (requires either a linked entity or a firm name); success
// navigates to the detail page.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { entitiesApi } from "@/api/counterparties";
import { useCreateTps } from "@/hooks/portfolioAdmin/useTps";
import { tpsEntityKeys } from "@/hooks/portfolioAdmin/tpsKeys";
import type { TpsCreate, TpsStatus } from "@/types/tps";
import { humanizeStatus } from "../../capital-markets/shared/format";

const STATUSES: TpsStatus[] = ["Active", "Inactive"];

export function NewTpsModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const create = useCreateTps();

  const [entityId, setEntityId] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [status, setStatus] = useState<TpsStatus>("Active");
  const [notes, setNotes] = useState("");

  const entitiesQuery = useQuery({
    queryKey: tpsEntityKeys.picker,
    queryFn: () => entitiesApi.list({ pageSize: 200 }),
  });
  const entities = useMemo(
    () => entitiesQuery.data?.data ?? [],
    [entitiesQuery.data],
  );

  const handleSave = () => {
    // #3-A — require either a linked entity OR a firm name.
    if (!entityId && !contactName.trim()) {
      toast.error("Enter a firm name, or link an entity.");
      return;
    }
    const body: TpsCreate = {
      entityId: entityId || null,
      status,
      notes: notes.trim() || null,
      contactName: contactName.trim() || null,
      contactEmail: contactEmail.trim() || null,
      contactPhone: contactPhone.trim() || null,
    };
    create
      .mutateAsync(body)
      .then((row) => {
        toast.success("TPS provider created.");
        onCreated(row.id);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="New TPS Provider"
      description="Create a TPS (Third-Party Servicer). Link a counterparty Entity, or just enter the firm's contact details."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={create.isPending}
            onClick={handleSave}
            data-testid="tps-create-submit"
          >
            Create TPS
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField
          label="Firm name"
          helperText="Required unless you link an entity below."
        >
          <Input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="e.g. Acme Servicing LLC"
            aria-label="Firm name"
            data-testid="tps-contact-name"
          />
        </FormField>

        <FormField label="Contact email">
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="ops@firm.example"
            aria-label="Contact email"
            data-testid="tps-contact-email"
          />
        </FormField>

        <FormField label="Contact phone">
          <Input
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="+1 480 555 1212"
            aria-label="Contact phone"
            data-testid="tps-contact-phone"
          />
        </FormField>

        <FormField label="Linked Entity (optional)">
          <Select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            aria-label="Linked Entity"
          >
            <option value="">No linked entity</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>
                {e.legalEntityName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Status">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as TpsStatus)}
            aria-label="Status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {humanizeStatus(s)}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            aria-label="Notes"
          />
        </FormField>
      </div>
    </Modal>
  );
}
